/**
 * Statsig event taxonomy — TypeScript source of truth (tutor-70k.7).
 *
 * Keep in sync with `shared/analytics/events.py`. The payload-guard
 * module (`tutor-70k.8`) imports `ALLOWED_PROPERTY_KEYS` to enforce
 * the whitelist on every `logEvent` call.
 *
 * See `shared/analytics/README.md` for category definitions and
 * trigger conditions.
 */

// --- Server-canonical events (emitted from api/) ---------------------------
// Listed here so the frontend can REFERENCE them when querying derived
// metrics or filtering Statsig dashboards, but the SPA must NEVER emit them
// — server events are server-canonical for tamper-resistance.
export const USER_SIGNED_UP = 'user_signed_up' as const
export const CHECKOUT_COMPLETED = 'checkout_completed' as const
export const ENTITLEMENT_GRANTED = 'entitlement_granted' as const
export const QUESTION_ASKED = 'question_asked' as const
export const ANSWER_SUBMITTED = 'answer_submitted' as const
export const GRADE_RETURNED = 'grade_returned' as const
export const GRADE_REPORTED = 'grade_reported' as const
export const QUOTA_EXHAUSTED = 'quota_exhausted' as const
// tutor-uox.2.a — fired by the API on every /feedback/general
// submission. Carries only the category dimension. Body / email
// never reach analytics.
export const FEEDBACK_SUBMITTED = 'feedback_submitted' as const

// Listed as literals (not identifier references) so the cross-language
// parity test (api/tests/test_event_taxonomy_parity.py) can scan them.
export const SERVER_EVENTS = [
  'user_signed_up',
  'checkout_completed',
  'entitlement_granted',
  'question_asked',
  'answer_submitted',
  'grade_returned',
  'grade_reported',
  'quota_exhausted',
  'feedback_submitted',
] as const

export type ServerEventName = (typeof SERVER_EVENTS)[number]

// --- Client-canonical events (emitted from frontend/) ----------------------
export const CHECKOUT_STARTED = 'checkout_started' as const
export const COURSE_CARD_VIEWED = 'course_card_viewed' as const
// Fired on every SessionView mount regardless of mode (practice /
// tutor / mock); the `mode` property carries the dimension. Replaces
// the earlier `tutor_chat_opened` which was misleading because
// SessionView covers all three modes.
export const SESSION_OPENED = 'session_opened' as const
export const DASHBOARD_VIEWED = 'dashboard_viewed' as const
// PE-9.4 — focus instrumentation. Client-canonical because the focus
// clock, the idle signal and the break decision all live on-device;
// the server only ever sees the aggregates the client PATCHes onto a
// focus block. All five are content-free: durations, counts, enums,
// booleans.
export const FOCUS_SESSION_STARTED = 'focus_session_started' as const
export const FOCUS_BLOCK_COMPLETED = 'focus_block_completed' as const
export const FOCUS_BREAK_TAKEN = 'focus_break_taken' as const
export const FOCUS_NUDGE_SHOWN = 'focus_nudge_shown' as const
export const FOCUS_SESSION_ENDED = 'focus_session_ended' as const

export const CLIENT_EVENTS = [
  'checkout_started',
  'course_card_viewed',
  'session_opened',
  'dashboard_viewed',
  'focus_session_started',
  'focus_block_completed',
  'focus_break_taken',
  'focus_nudge_shown',
  'focus_session_ended',
] as const

export type ClientEventName = (typeof CLIENT_EVENTS)[number]

export const ALL_EVENTS = [...SERVER_EVENTS, ...CLIENT_EVENTS] as const
export type EventName = (typeof ALL_EVENTS)[number]

// --- Property whitelist ----------------------------------------------------
// Every metadata key passed to logEvent MUST be in this set. The
// payload-guard module rejects unknown keys and keys carrying free
// text (answer/question/grade/email/comment). PII gate.
export const ALLOWED_PROPERTY_KEYS = [
  'course_id',
  'board',
  'level',
  'marks',
  'question_type',
  'topic',
  'mode',
  'model',
  'prompt_version_hash',
  'prompt_path',
  'rating',
  'image_turn',
  'regrade_count',
  // tutor-uox.2.a — feedback_submitted dimension.
  // Enum value: bug|suggestion|praise|other.
  'category',
  // PE-9.4 — focus block dimensions. Enums, counts, durations and
  // booleans only. The reflection a student writes on the completion
  // screen and any free text typed into a goal are local-only
  // (PE-9.5): they must never reach analytics, so no key here is ever
  // a text field.
  'ended_reason', // goal_met|break_taken|ended_early|abandoned
  'goal_type', // time|marks
  'goal_met', // bool
  'stretch_selected', // bool — third (stretch) launcher option
  'active_seconds', // int — aggregate, server-clamped
  'idle_seconds', // int — aggregate only, never keystrokes
  'block_index', // int — 0-based position within a session
  'capacity_minutes', // int — derived median, whole minutes
  'stage', // focus stage: hazel|rowan|birch|oak|sequoia
] as const

export type AllowedPropertyKey = (typeof ALLOWED_PROPERTY_KEYS)[number]
export type EventProperties = Partial<
  Record<AllowedPropertyKey, string | number | boolean>
>
